/**
 * Currency Detection and Conversion Service
 * Detects user's local currency via IP geolocation and provides conversion
 */

// Country to currency mapping
const COUNTRY_CURRENCIES = {
  // African Countries
  'NG': { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  'GH': { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi' },
  'KE': { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  'ZA': { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  'EG': { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound' },
  'MA': { code: 'MAD', symbol: 'MAD', name: 'Moroccan Dirham' },
  'TZ': { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling' },
  'UG': { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling' },
  'ET': { code: 'ETB', symbol: 'Br', name: 'Ethiopian Birr' },
  'RW': { code: 'RWF', symbol: 'FRw', name: 'Rwandan Franc' },
  'SN': { code: 'XOF', symbol: 'CFA', name: 'West African CFA Franc' },
  'CI': { code: 'XOF', symbol: 'CFA', name: 'West African CFA Franc' },
  'CM': { code: 'XAF', symbol: 'FCFA', name: 'Central African CFA Franc' },
  'AO': { code: 'AOA', symbol: 'Kz', name: 'Angolan Kwanza' },
  'ZM': { code: 'ZMW', symbol: 'ZK', name: 'Zambian Kwacha' },
  'ZW': { code: 'ZWL', symbol: 'Z$', name: 'Zimbabwean Dollar' },
  'BW': { code: 'BWP', symbol: 'P', name: 'Botswana Pula' },
  'MZ': { code: 'MZN', symbol: 'MT', name: 'Mozambican Metical' },
  'DZ': { code: 'DZD', symbol: 'DA', name: 'Algerian Dinar' },
  'TN': { code: 'TND', symbol: 'DT', name: 'Tunisian Dinar' },
  'LY': { code: 'LYD', symbol: 'LD', name: 'Libyan Dinar' },
  'SD': { code: 'SDG', symbol: 'SDG', name: 'Sudanese Pound' },
  'CD': { code: 'CDF', symbol: 'FC', name: 'Congolese Franc' },
  'MU': { code: 'MUR', symbol: 'Rs', name: 'Mauritian Rupee' },
  'NA': { code: 'NAD', symbol: 'N$', name: 'Namibian Dollar' },
  
  // Major Global Currencies
  'US': { code: 'USD', symbol: '$', name: 'US Dollar' },
  'GB': { code: 'GBP', symbol: '£', name: 'British Pound' },
  'EU': { code: 'EUR', symbol: '€', name: 'Euro' },
  'DE': { code: 'EUR', symbol: '€', name: 'Euro' },
  'FR': { code: 'EUR', symbol: '€', name: 'Euro' },
  'IT': { code: 'EUR', symbol: '€', name: 'Euro' },
  'ES': { code: 'EUR', symbol: '€', name: 'Euro' },
  'NL': { code: 'EUR', symbol: '€', name: 'Euro' },
  'BE': { code: 'EUR', symbol: '€', name: 'Euro' },
  'CN': { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  'JP': { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  'IN': { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  'AE': { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
  'SA': { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal' },
  'AU': { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  'CA': { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  'BR': { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  'SG': { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  'MY': { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  'TH': { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  'TR': { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  'RU': { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  'KR': { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
};

// Default currency
const DEFAULT_CURRENCY = { code: 'USD', symbol: '$', name: 'US Dollar' };

// Cache for exchange rates (expires after 1 hour)
let exchangeRatesCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

// Cache for user currency detection
const USER_CURRENCY_KEY = 'afriqoma_user_currency';
const USER_COUNTRY_KEY = 'afriqoma_user_country';

/**
 * Detect user's country from IP address using free API
 */
export async function detectUserCountry() {
  // Check cache first
  const cached = sessionStorage.getItem(USER_COUNTRY_KEY);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (error) {
      // Invalid cache - log and continue with fresh detection
      if (process.env.NODE_ENV === 'development') {
        console.warn('[CurrencyService] Invalid country cache:', error.message);
      }
    }
  }

  try {
    // Try ipapi.co (free, supports HTTPS, 1000 req/day)
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(5000)
    });
    
    if (response.ok) {
      const data = await response.json();
      const result = {
        countryCode: data.country_code,
        country: data.country_name,
        city: data.city
      };
      sessionStorage.setItem(USER_COUNTRY_KEY, JSON.stringify(result));
      return result;
    }
  } catch (error) {
    // IP detection failed - log in dev, use defaults
    if (process.env.NODE_ENV === 'development') {
      console.warn('[CurrencyService] IP detection failed:', error.message);
    }
  }

  // Default to US if all detection fails
  return { countryCode: 'US', country: 'United States', city: null };
}

/**
 * Get currency info for a country code
 */
export function getCurrencyForCountry(countryCode) {
  return COUNTRY_CURRENCIES[countryCode?.toUpperCase()] || DEFAULT_CURRENCY;
}

/**
 * Detect user's local currency
 */
export async function detectUserCurrency() {
  // Check cache first
  const cached = sessionStorage.getItem(USER_CURRENCY_KEY);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (error) {
      // Invalid cache - log and continue with fresh detection
      if (process.env.NODE_ENV === 'development') {
        console.warn('[CurrencyService] Invalid currency cache:', error.message);
      }
    }
  }

  const location = await detectUserCountry();
  const currency = getCurrencyForCountry(location.countryCode);
  
  const result = {
    ...currency,
    countryCode: location.countryCode,
    country: location.country
  };
  
  sessionStorage.setItem(USER_CURRENCY_KEY, JSON.stringify(result));
  return result;
}

/**
 * Fetch exchange rates (USD base)
 */
export async function getExchangeRates() {
  // Check cache
  if (exchangeRatesCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
    return exchangeRatesCache;
  }

  try {
    // Using exchangerate-api.com free tier
    const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD', {
      signal: AbortSignal.timeout(10000)
    });
    
    if (response.ok) {
      const data = await response.json();
      exchangeRatesCache = data.rates;
      cacheTimestamp = Date.now();
      return data.rates;
    }
  } catch (error) {
    // Exchange rate fetch failed - log in dev, use fallback rates
    if (process.env.NODE_ENV === 'development') {
      console.warn('[CurrencyService] Exchange rate fetch failed:', error.message);
    }
  }

  // Fallback rates (approximate, updated periodically)
  return {
    USD: 1,
    NGN: 1550,
    GHS: 15.5,
    KES: 155,
    ZAR: 18.5,
    EGP: 49,
    GBP: 0.79,
    EUR: 0.92,
    CNY: 7.25,
    INR: 83.5,
    AED: 3.67,
    XOF: 605,
    XAF: 605,
    TZS: 2700,
    UGX: 3850,
    ETB: 57,
    RWF: 1350,
    ZMW: 27,
    MAD: 10,
    AOA: 835,
    MZN: 64,
    BWP: 13.5,
    MUR: 46
  };
}

/**
 * Convert amount from one currency to another
 */
export async function convertCurrency(amount, fromCurrency = 'USD', toCurrency = 'USD') {
  if (fromCurrency === toCurrency) return amount;
  
  const rates = await getExchangeRates();
  
  // Convert to USD first (if not already)
  let amountInUSD = amount;
  if (fromCurrency !== 'USD') {
    amountInUSD = amount / (rates[fromCurrency] || 1);
  }
  
  // Convert from USD to target currency
  const convertedAmount = amountInUSD * (rates[toCurrency] || 1);
  return convertedAmount;
}

/**
 * Format price with currency symbol
 */
export function formatPrice(amount, currencyCode = 'USD', options = {}) {
  const { showCode = false, decimals = 2 } = options;
  
  const currency = Object.values(COUNTRY_CURRENCIES).find(c => c.code === currencyCode) || DEFAULT_CURRENCY;
  
  // Format number with appropriate decimals
  const formattedAmount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(amount);
  
  if (showCode) {
    return `${currency.symbol}${formattedAmount} ${currencyCode}`;
  }
  
  return `${currency.symbol}${formattedAmount}`;
}

/**
 * Format price in user's local currency
 */
export async function formatLocalPrice(amountInUSD, options = {}) {
  const userCurrency = await detectUserCurrency();
  const localAmount = await convertCurrency(amountInUSD, 'USD', userCurrency.code);
  return formatPrice(localAmount, userCurrency.code, options);
}

/**
 * Get all supported currencies for selection
 */
export function getSupportedCurrencies() {
  const uniqueCurrencies = new Map();
  
  Object.values(COUNTRY_CURRENCIES).forEach(currency => {
    if (!uniqueCurrencies.has(currency.code)) {
      uniqueCurrencies.set(currency.code, currency);
    }
  });
  
  return Array.from(uniqueCurrencies.values()).sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * Clear cached currency data (useful when user manually changes currency)
 */
export function clearCurrencyCache() {
  sessionStorage.removeItem(USER_CURRENCY_KEY);
  sessionStorage.removeItem(USER_COUNTRY_KEY);
  exchangeRatesCache = null;
  cacheTimestamp = null;
}

export { COUNTRY_CURRENCIES, DEFAULT_CURRENCY };
